import { Fragment, type ReactElement } from "react";
import { Navigate, Route, useLocation } from "react-router-dom";
import { routes } from "routing";

/**
 * Old `/authentication/*` paths mapped to their canonical `/auth/*` registry paths. Permanent,
 * because the URLs live in inboxes indefinitely. The deeplink resolver reads the same mapping.
 */
export const LEGACY_AUTH_REDIRECTS: ReadonlyArray<readonly [from: string, to: string]> = [
  ["/authentication/signIn", routes.auth.signIn.path],
  ["/authentication/passwordlessEmailSent", routes.auth.signInEmailSent.path],
  ["/authentication/forgotPassword", routes.auth.forgotPassword.path],
  ["/authentication/recoveryEmailSent", routes.auth.forgotPasswordEmailSent.path],
  ["/authentication/signUp", routes.auth.signUp.path],
  ["/authentication/signUpEmailSent", routes.auth.signUpEmailSent.path],
  ["/authentication/addDetails", routes.auth.signUpDetails.path],
  ["/authentication/setPassword", routes.auth.signUpPassword.path],
  ["/authentication/addProfilePic", routes.auth.signUpProfilePicture.path],
  ["/authentication/termsAndPolicies", routes.auth.terms.path],
  ["/authentication/emailConfirmation", routes.auth.confirmEmail.path],
  ["/authentication/joinCreateOrg", routes.auth.organisation.path],
  ["/authentication/orgDetails", routes.auth.organisationDetails.path],
  ["/authentication/teamName", routes.auth.teamName.path],
  ["/authentication/requestAccess", routes.auth.requestAccess.path],
  ["/authentication/requestAccessEmail", routes.auth.requestAccessEmailSent.path],
  ["/authentication/setupComplete", routes.auth.complete.path],
  ["/authentication/notSameUser", routes.auth.notSameUser.path],
  ["/authentication/joined", routes.auth.joined.path],
  ["/authentication/joinlink", routes.auth.join.path],
];

/**
 * Redirect that carries the query string and hash across to the new path. Emailed
 * links append `?email=`, `?org=`, `?data=` etc., which the destination screens read,
 * so a bare <Navigate to="…"> (which drops search/hash) would lose them.
 */
function LegacyRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/**
 * The legacy auth redirects as <Route> elements, for dropping into the app's
 * <Routes> tree (mirrors renderRoutes: a Fragment so React Router flattens it).
 */
export function legacyAuthRedirects(): ReactElement {
  return (
    <Fragment>
      {LEGACY_AUTH_REDIRECTS.map(([from, to]) => (
        <Route key={from} path={from} element={<LegacyRedirect to={to} />} />
      ))}
    </Fragment>
  );
}
